import { saveAndGetImgURL } from '@store/firebase/storage';
import { useEffect } from 'react';
import Editor from 'react-quill';

// 파일 검증 함수 정의 (validateFile.ts 파일로 분리 가능)
export function validateFile(
  fileType: string,
  fileSize: number,
  fileName: string,
): void {
  const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB 제한

  // 지원되는 이미지 타입
  const supportedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

  if (!supportedTypes.includes(fileType)) {
    throw new Error(`지원하지 않는 파일 형식입니다: ${fileName}`);
  }

  if (fileSize > MAX_FILE_SIZE) {
    throw new Error(`파일 크기가 너무 큽니다. 최대 허용 크기: 20MB`);
  }
}

type PasteHandlerProps = {
  quillRef: React.RefObject<Editor>;
  collectionPath: string;
  setImgURLList?: React.Dispatch<React.SetStateAction<string[]>>;
};

// 커서 고정: focus → setSelection('user') → rAF → 0ms → 16ms
function stabilizeSelection(editor: any, index: number) {
  try {
    editor.focus?.();
  } catch (_) {}
  try {
    editor.setSelection({ index, length: 0 }, 'user');
  } catch (_) {}
  try {
    requestAnimationFrame(() => {
      try {
        editor.setSelection({ index, length: 0 }, 'user');
      } catch (_) {}
    });
  } catch (_) {}
  setTimeout(() => {
    try {
      editor.setSelection({ index, length: 0 }, 'user');
    } catch (_) {}
  }, 0);
  setTimeout(() => {
    try {
      editor.setSelection({ index, length: 0 }, 'user');
    } catch (_) {}
  }, 16);
}

// 📍 이미지 처리 함수
const handleImagePaste = async (
  editor: any,
  files: FileList,
  collectionPath: string,
  setImgURLList?: React.Dispatch<React.SetStateAction<string[]>>,
) => {
  console.log('📷 handleImagePaste: 이미지 처리 시작');

  const selectedFiles = Array.from(files);
  const cursorPosition = editor.getSelection(true)?.index || 0;

  for (let i = 0; i < selectedFiles.length; i++) {
    const file = selectedFiles[i];

    if (file.type.startsWith('image/')) {
      try {
        validateFile(file.type, file.size, file.name);

        const imgURL: string = await saveAndGetImgURL(collectionPath, file);

        if (setImgURLList) {
          setImgURLList((prev: string[]) => [...prev, imgURL]);
        }

        editor.insertEmbed(cursorPosition + i, 'image', imgURL);
        editor.setSelection({ index: cursorPosition + i + 1, length: 0 });

        console.log('✅ 이미지 삽입 완료:', imgURL);
      } catch (err: any) {
        alert(err.message);
      }
    }
  }
};

// 📍 Twitter URL 처리 함수 (직접 XEmbedBlot 삽입)
const handleTwitterUrlPaste = (editor: any, pastedText: string) => {
  console.log('🐦 handleTwitterUrlPaste: Twitter URL 직접 삽입 처리');

  // 전역 비활성 플래그 확인 (HTML 모드 포함)
  const isDisabled = (window as any).__disableTwitterEmbed === true;
  if (isDisabled) {
    console.log('🚫 Twitter Embed 비활성화 상태 → 일반 텍스트로 삽입');
    const range = editor.getSelection(true);
    if (range) {
      const beforeLen = editor.getLength();
      editor.insertText(range.index, pastedText);
      const afterLen = editor.getLength();
      const insertedLen = Math.max(0, afterLen - beforeLen);
      const nextIndex = range.index + insertedLen;
      stabilizeSelection(editor, nextIndex);
      return true;
    }
    return false;
  }

  try {
    const range = editor.getSelection(true);
    if (range) {
      // 📍 직접 XEmbedBlot으로 삽입
      console.log('✅ Twitter URL을 XEmbedBlot으로 삽입:', {
        text: pastedText,
        index: range.index,
      });
      const prevDisable = (window as any).__disableTwitterEmbed === true;
      (window as any).__disableTwitterEmbed = true; // selection 고정 우선
      editor.insertEmbed(range.index, 'x', pastedText);
      stabilizeSelection(editor, range.index + 1);
      setTimeout(() => {
        (window as any).__disableTwitterEmbed = prevDisable;
      }, 60);
      console.log('✅ Twitter URL 삽입 완료');
      return true; // preventDefault 필요
    }
  } catch (error) {
    console.error('❌ Twitter URL 삽입 실패:', error);
    // 실패하면 일반 텍스트로 삽입
    const range = editor.getSelection(true);
    if (range) {
      const beforeLen = editor.getLength();
      editor.insertText(range.index, pastedText);
      const afterLen = editor.getLength();
      const insertedLen = Math.max(0, afterLen - beforeLen);
      const nextIndex = range.index + insertedLen;
      stabilizeSelection(editor, nextIndex);
    }
    return true; // preventDefault 필요
  }

  return false;
};

// 📍 일반 텍스트 처리 함수
const handlePlainTextPaste = (editor: any, pastedText: string) => {
  console.log('📝 handlePlainTextPaste: 일반 텍스트 처리');

  const range = editor.getSelection(true);
  if (range) {
    try {
      const DeltaCtor = (window as any).Quill?.import?.('delta');
      const delta = editor.clipboard.convert(pastedText);
      if (DeltaCtor && delta) {
        const d = new DeltaCtor();
        editor.updateContents(
          d.retain(range.index).delete(range.length).concat(delta),
          'user',
        );
        const insertedLen =
          typeof delta.length === 'function'
            ? delta.length()
            : pastedText?.length || 0;
        const nextIndex = range.index + insertedLen;
        stabilizeSelection(editor, nextIndex);
      } else {
        const beforeLen = editor.getLength();
        editor.insertText(range.index, pastedText);
        const afterLen = editor.getLength();
        const insertedLen = Math.max(0, afterLen - beforeLen);
        const newIndex = range.index + insertedLen;
        stabilizeSelection(editor, newIndex);
      }
    } catch (_) {
      const beforeLen = editor.getLength();
      editor.insertText(range.index, pastedText);
      const afterLen = editor.getLength();
      const insertedLen = Math.max(0, afterLen - beforeLen);
      const newIndex = range.index + insertedLen;
      stabilizeSelection(editor, newIndex);
    }

    return true; // preventDefault 필요
  }
  return false;
};

// 📍 통합 Paste 핸들러
const useUnifiedPasteHandler = ({
  quillRef,
  collectionPath,
  setImgURLList,
}: PasteHandlerProps) => {
  useEffect(() => {
    if (!quillRef?.current) {
      return;
    }

    const editor = quillRef.current.getEditor();
    if (!editor) {
      return;
    }

    const handlePaste = async (e: ClipboardEvent) => {
      const clipboardData = e.clipboardData;
      if (!clipboardData) {
        return;
      }

      const files = clipboardData.files;
      const pastedText = clipboardData.getData('text/plain');

      // 🎯 1순위: 이미지 파일
      if (files && files.length > 0) {
        const hasImages = Array.from(files).some((file) =>
          file.type.startsWith('image/'),
        );
        if (hasImages) {
          console.log('📷 이미지 파일 감지 → handleImagePaste 호출');
          e.preventDefault();
          await handleImagePaste(editor, files, collectionPath, setImgURLList);
          return;
        }
      }

      // 🎯 2순위: Twitter URL (HTML 모드/비활성화 시 일반 텍스트 처리)
      if (pastedText) {
        const trimmedText = pastedText.trim();
        const twitterUrlPattern =
          /^https:\/\/(x\.com|twitter\.com)\/\w+\/status\/\d+/;
        const isTwitterUrl = twitterUrlPattern.test(trimmedText);

        if (isTwitterUrl) {
          const shouldPreventDefault = handleTwitterUrlPaste(
            editor,
            trimmedText,
          );
          if (shouldPreventDefault) {
            e.preventDefault();
          }
          return;
        }
      }

      // 🎯 3순위: 일반 텍스트는 Quill 기본 동작에 맡김
      // (preventDefault하지 않음 → 중복 붙여넣기 방지)

      // 🎯 4순위: 기타 URL (일반 링크 등) - Quill 기본 동작
    };

    try {
      editor.root.addEventListener('paste', handlePaste);
    } catch (error) {
      console.error('❌ 통합 Paste 핸들러: 이벤트 리스너 등록 실패', error);
    }

    return () => {
      try {
        editor.root.removeEventListener('paste', handlePaste);
      } catch (error) {
        console.error('❌ 통합 Paste 핸들러: 이벤트 리스너 해제 실패', error);
      }
    };
  }, [quillRef, collectionPath, setImgURLList]);
};

export default useUnifiedPasteHandler;
